import clsx from 'clsx';
import { CouterPatternStoreMODisplay } from 'features/CounterPattern/models';
import React, { useEffect, useState } from 'react';
import { Button, Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { FaSort, FaSortDown, FaSortUp } from 'react-icons/fa';

export interface CounterPatternStoreTableProps {
  data: CouterPatternStoreMODisplay[];
  onOpenMobileOrderDisplayModal?: (
    restaurantId: number,
    restaurantName: string,
    restaurantCode: string,
    mobileOrderDisplayFlg: boolean
  ) => void;
}

enum SortType {
  ASC = 'ASC',
  DESC = 'DESC',
}

enum Columns {
  CD = 'cd',
  NAME = 'name',
  COUNTER_PATTERN_CD = 'counter_pattern_cd',
  MO_DISPLAY_FLG = 'mo_display_flg',
}

function CounterPatternStoreTable({ data, onOpenMobileOrderDisplayModal }: CounterPatternStoreTableProps) {
  const { t } = useTranslation();

  // state
  const [sortedData, setSortedData] = useState<Array<{ [key: string]: any }>>([]);
  const [sortBy, setSortBy] = useState<{ sortName: string; sortType: SortType | null }>({
    sortName: Columns.CD,
    sortType: SortType.ASC,
  });

  function handleOpenMobileOrderDisplayModal(
    restaurantId: number,
    restaurantName: string,
    restaurantCode: string,
    mobileOrderDisplayFlg: boolean
  ) {
    onOpenMobileOrderDisplayModal?.(restaurantId, restaurantName, restaurantCode, mobileOrderDisplayFlg);
  }

  function renderSortStatus(column: string) {
    if (column === sortBy.sortName) {
      if (sortBy.sortType === SortType.DESC) {
        return <FaSortDown size={16} />;
      } else if (sortBy.sortType === SortType.ASC) {
        return <FaSortUp size={16} />;
      }
    }

    return <FaSort size={16} />;
  }

  function sortData() {
    if (sortBy.sortType === null) {
      return [...data];
    }

    return [...data].sort((a, b) => {
      if (sortBy.sortName === Columns.NAME) {
        if (sortBy.sortType === SortType.ASC) {
          return a.name.localeCompare(b.name, 'ja');
        } else if (sortBy.sortType === SortType.DESC) {
          return b.name.localeCompare(a.name, 'ja');
        }
      }

      if (sortBy.sortName === Columns.COUNTER_PATTERN_CD) {
        if (sortBy.sortType === SortType.ASC) {
          return Number(a.counter_pattern_cd) - Number(b.counter_pattern_cd);
        } else if (sortBy.sortType === SortType.DESC) {
          return Number(b.counter_pattern_cd) - Number(a.counter_pattern_cd);
        }
      }

      if (sortBy.sortName === Columns.MO_DISPLAY_FLG) {
        if (sortBy.sortType === SortType.ASC) {
          return Number(a.mo_display_flg) - Number(b.mo_display_flg);
        } else if (sortBy.sortType === SortType.DESC) {
          return Number(b.mo_display_flg) - Number(a.mo_display_flg);
        }
      }

      if (sortBy.sortName === Columns.CD) {
        if (sortBy.sortType === SortType.ASC) {
          return Number(a.cd) - Number(b.cd);
        } else if (sortBy.sortType === SortType.DESC) {
          return Number(b.cd) - Number(a.cd);
        }
      }

      return 0;
    });
  }

  function handleSortByCol(col: Columns) {
    if (sortBy.sortName !== col) {
      setSortBy({
        sortName: col,
        sortType: SortType.ASC,
      });
    } else {
      let sortType: SortType | null = null;
      if (sortBy.sortType === SortType.ASC) {
        sortType = SortType.DESC;
      } else if (sortBy.sortType === SortType.DESC) {
        sortType = null;
      } else {
        sortType = SortType.ASC;
      }
      setSortBy({
        sortName: col,
        sortType: sortType,
      });
    }
  }

  // effects
  useEffect(() => {
    const sorted = sortData();
    setSortedData(sorted);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, sortBy]);

  return (
    <Table className="text-center" hover>
      <thead>
        <tr>
          <th
            rowSpan={2}
            className="position-relative cursor-pointer"
            style={{ width: '20%' }}
            onClick={handleSortByCol.bind(null, Columns.CD)}
          >
            <span>{t('CounterPatternStoreMobileOrderDisplayModal.store_code')}</span>
            <div style={{ position: 'absolute', right: '5px', bottom: '7px' }}>{renderSortStatus(Columns.CD)}</div>
          </th>
          <th
            rowSpan={2}
            className="position-relative cursor-pointer"
            style={{ width: '40%' }}
            onClick={handleSortByCol.bind(null, Columns.NAME)}
          >
            <span>{t('CounterPatternStoreMobileOrderDisplayModal.store_name')}</span>
            <div style={{ position: 'absolute', right: '5px', bottom: '7px' }}>{renderSortStatus(Columns.NAME)}</div>
          </th>
          <th colSpan={2}>{t('CounterPatternStoreMobileOrderDisplayModal.display')}</th>
        </tr>
        <tr>
          <th
            className="position-relative cursor-pointer"
            onClick={handleSortByCol.bind(null, Columns.COUNTER_PATTERN_CD)}
          >
            <span>{t('CounterPatternStoreMobileOrderDisplayModal.counter_pattern')}</span>
            <div style={{ position: 'absolute', right: '5px', bottom: '7px' }}>
              {renderSortStatus(Columns.COUNTER_PATTERN_CD)}
            </div>
          </th>
          <th className="position-relative cursor-pointer" onClick={handleSortByCol.bind(null, Columns.MO_DISPLAY_FLG)}>
            <span>{t('CounterPatternStoreMobileOrderDisplayModal.action_mo')}</span>
            <div style={{ position: 'absolute', right: '5px', bottom: '7px' }}>
              {renderSortStatus(Columns.MO_DISPLAY_FLG)}
            </div>
          </th>
        </tr>
      </thead>
      <tbody>
        {sortedData.map(function (item) {
          return (
            <tr key={item.id}>
              <td>{item.cd}</td>
              <td>{item.name}</td>
              <td>{item.counter_pattern_cd}</td>
              <td>
                <Button
                  className={clsx({
                    'is-mo-setting': item.is_user_setting,
                    'btn--dark-green': item.mo_display_flg,
                    'btn--green': !item.mo_display_flg,
                  })}
                  onClick={handleOpenMobileOrderDisplayModal.bind(
                    null,
                    item.id,
                    item.name,
                    item.cd,
                    item.mo_display_flg
                  )}
                >
                  {item.mo_display_flg ? t('StoreSettingDetailPage.enable') : t('StoreSettingDetailPage.disable')}
                </Button>
              </td>
            </tr>
          );
        })}
      </tbody>
    </Table>
  );
}

export default React.memo(CounterPatternStoreTable);
